import type {
  Cube,
  DimensionFormat,
  MetaResponse,
  TCubeMeasure,
  TCubeNestedFolder,
  UnaryOperator,
  ViewGroup,
} from "@cubejs-client/core";
import { z } from "zod/v4";
import { getInsightsChannel } from "../../insights-channels/registry";
import { youtubeThumbnailUrl } from "../../youtube";
import { BINARY_OPERATORS } from "../constants";

export const UNARY_OPERATORS = [
  "set",
  "notSet",
] as const satisfies readonly UnaryOperator[];

export const Operator = z.enum([...BINARY_OPERATORS, ...UNARY_OPERATORS]);
export type Operator = z.infer<typeof Operator>;

export const MetricFormat = z.enum([
  "currency-compact",
  "currency-precise",
  "number-compact",
  "percent",
  "frequency",
]);
export type MetricFormat = z.infer<typeof MetricFormat>;

export const MetricPolarity = z.enum(["higher", "lower", "neutral"]);
export type MetricPolarity = z.infer<typeof MetricPolarity>;

export const ValueLabel = z.object({ value: z.string(), label: z.string() });
export type ValueLabel = z.infer<typeof ValueLabel>;

export const Grain = z.enum(["ad", "ad_set", "campaign"]);
export type Grain = z.infer<typeof Grain>;

export const MeasurePlace = z.enum([
  "metric",
  "default-metric",
  "card-metric",
  "drill-tile",
]);
export type MeasurePlace = z.infer<typeof MeasurePlace>;

export const WhenEmpty = z.enum(["hide-column", "show-dash"]);

export const Describes = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("entity"), grain: Grain }),
  z.object({ kind: z.literal("scope") }),
  z.object({ kind: z.literal("conversion") }),
]);
export type Describes = z.infer<typeof Describes>;

export const SegmentInitially = z.enum(["on", "off"]);

const nonEmpty = <T extends z.ZodType>(item: T) => z.tuple([item], item);
const ListedValues = nonEmpty(ValueLabel);

const unsupportedWhenFilled = (name: string) =>
  z.array(z.string()).refine((items) => items.length === 0, { message: name });

export const precision = z.literal([0, 1, 2, 3, 4, 5, 6]);
const namedNumericFormat = z.union([
  z.templateLiteral([
    z.enum(["number", "percent", "currency", "decimal", "abbr", "accounting"]),
    "_",
    precision,
  ]),
  z.enum(["decimal", "abbr", "accounting"]),
]);
export const dimensionFormat = z.union([
  z.enum(["percent", "currency", "number", "imageUrl", "id", "link"]),
  namedNumericFormat,
  z.object({ type: z.literal("link"), label: z.string() }),
  z.object({ type: z.literal("custom-time"), value: z.string() }),
  z.object({
    type: z.literal("custom-numeric"),
    value: z.string(),
    alias: z.string().optional(),
  }),
]) satisfies z.ZodType<DimensionFormat>;

type RefKind =
  | "time"
  | "dimension"
  | "group-dimension"
  | "number-dimension"
  | "measure"
  | "metric"
  | "value-list-measure"
  | "creative-media-measure"
  | "nested-measure"
  | "segment";
type Ref = (kind: RefKind) => z.ZodType<string, string>;

const controlsSchema = (ref: Ref) =>
  z.object({
    dateRange: z.object({ date: ref("time") }),
    channelPicker: z.object({
      sync: ref("dimension"),
      account: z.object({
        member: ref("dimension"),
        name: ref("dimension"),
      }),
      conversion: z.object({
        channel: ref("dimension"),
        event: ref("dimension"),
        given: z.string(),
        count: ref("measure"),
      }),
    }),
    sort: z.object({ default: ref("metric") }),
  });

const groupByTargetsSchema = (ref: Ref) => ({
  attribute: z.object({
    kind: z.literal("attribute"),
    member: ref("group-dimension"),
    segments: z.array(ref("segment")).default([]),
  }),
  none: z.object({ kind: z.literal("none") }),
  entities: z.object({
    kind: z.literal("entities"),
    grain: Grain.exclude(["ad"]),
  }),
  creativeTags: z.object({ kind: z.literal("creative-tags") }),
});

const groupByTargetSchema = (ref: Ref) => {
  const { attribute, none, entities, creativeTags } = groupByTargetsSchema(ref);
  return z.discriminatedUnion("kind", [
    attribute,
    none,
    entities,
    creativeTags,
  ]);
};

const featuredGroupBySchema = (ref: Ref) => {
  const { attribute, none, entities, creativeTags } = groupByTargetsSchema(ref);
  const subtitled = { subtitle: z.string() };
  return z.discriminatedUnion("kind", [
    attribute.extend({ label: z.string(), ...subtitled }),
    none.extend(subtitled),
    entities.extend(subtitled),
    creativeTags.extend(subtitled),
  ]);
};

export type GroupByTarget = z.output<ReturnType<typeof groupByTargetSchema>>;
export type FeaturedGroupBy = z.output<
  ReturnType<typeof featuredGroupBySchema>
>;

const targetKeyOf = (target: GroupByTarget): string => {
  switch (target.kind) {
    case "attribute": {
      return target.member;
    }
    case "entities": {
      return target.grain;
    }
    case "none":
    case "creative-tags": {
      return target.kind;
    }
    default: {
      return target satisfies never;
    }
  }
};

const groupByMenuSchema = (ref: Ref) =>
  z
    .object({
      featured: z.array(featuredGroupBySchema(ref)),
      more: z.array(groupByTargetSchema(ref)),
    })
    .superRefine(({ featured, more }, context) => {
      const keys = [...featured, ...more].map(targetKeyOf);
      for (const duplicate of new Set(
        keys.filter((key, index) => keys.indexOf(key) !== index),
      )) {
        context.addIssue({
          code: "custom",
          message: `Group by lists ${duplicate} more than once`,
        });
      }
    });

const adLayoutsSchema = (ref: Ref) =>
  z.object({
    card: z.object({
      media: ref("dimension"),
      creatives: ref("number-dimension"),
    }),
    table: z.object({
      status: ref("dimension"),
      objective: ref("dimension"),
    }),
    groupCard: z.object({
      count: ref("measure"),
      statuses: ref("value-list-measure"),
      objectives: ref("value-list-measure"),
      creatives: ref("creative-media-measure"),
      previews: ref("nested-measure"),
      nameFromAd: ref("group-dimension"),
    }),
    drawer: z.object({
      copy: z.object({
        primaryText: ref("dimension"),
        headline: ref("dimension"),
        description: ref("dimension"),
        callToAction: ref("dimension"),
        destinationUrl: ref("dimension"),
      }),
    }),
    fatigue: z.object({
      status: ref("dimension"),
      fatiguing: nonEmpty(z.string()),
      peakCtr: ref("number-dimension"),
      vsPeak: ref("number-dimension"),
      wowCtrChange: ref("number-dimension"),
      cause: ref("dimension"),
      daysActive: ref("number-dimension"),
      ctr: ref("metric"),
      frequency: ref("metric"),
      spend: ref("metric"),
    }),
    groupBy: groupByMenuSchema(ref),
  });

const adSetLayoutsSchema = (ref: Ref) =>
  z.object({ table: z.object({ status: ref("dimension") }) });

const campaignLayoutsSchema = (ref: Ref) =>
  z.object({
    table: z.object({
      status: ref("dimension"),
      objective: ref("dimension"),
    }),
  });

const entitySchema = (ref: Ref) =>
  z.object({ id: ref("dimension"), name: ref("dimension") });

const adEntitiesSchema = (ref: Ref) =>
  z.object({
    campaign: entitySchema(ref),
    adSet: entitySchema(ref),
    ad: entitySchema(ref),
  });

const adSetEntitiesSchema = (ref: Ref) =>
  z.object({ campaign: entitySchema(ref), adSet: entitySchema(ref) });

const campaignEntitiesSchema = (ref: Ref) =>
  z.object({ campaign: entitySchema(ref) });

export type EntitiesSchema = z.ZodType<
  Record<string, { id: string; name: string }>
>;

type Records<Layouts extends z.ZodType, Entities extends EntitiesSchema> = {
  layouts: (ref: Ref) => Layouts;
  entities: (ref: Ref) => Entities;
};

export const VIEW_RECORDS = {
  ad_performance: { layouts: adLayoutsSchema, entities: adEntitiesSchema },
  ad_set_performance: {
    layouts: adSetLayoutsSchema,
    entities: adSetEntitiesSchema,
  },
  campaign_performance: {
    layouts: campaignLayoutsSchema,
    entities: campaignEntitiesSchema,
  },
};

export const viewRecordsSchema = <
  Layouts extends z.ZodType,
  Entities extends EntitiesSchema,
>({
  records,
  ref,
}: {
  records: Records<Layouts, Entities>;
  ref: Ref;
}) =>
  z.object({
    controls: controlsSchema(ref),
    layouts: records.layouts(ref),
    entities: records.entities(ref),
  });

// The drill path runs campaign, ad set, ad: the order every entities schema declares its keys in.
export const drillLevelsOf = (entities: Record<string, { id: string }>) =>
  Object.values(entities).map((entity) => entity.id);

const anyName: Ref = () => z.string();

export type Controls = z.output<ReturnType<typeof controlsSchema>>;
export type AdLayouts = z.output<ReturnType<typeof adLayoutsSchema>>;
export type AdSetLayouts = z.output<ReturnType<typeof adSetLayoutsSchema>>;
export type CampaignLayouts = z.output<
  ReturnType<typeof campaignLayoutsSchema>
>;

const ViewMetaSchema = z.object({
  syncs: nonEmpty(
    z.object({
      id: z.string(),
      channel: z
        .string()
        .refine((channel) => getInsightsChannel(channel) !== undefined, {
          message: "Unknown insights channel",
        }),
    }),
  ),
  defaultEvents: z.record(z.string(), nonEmpty(z.string())),
  controls: controlsSchema(anyName),
});
export type ViewMeta = z.output<typeof ViewMetaSchema>;

const filtering = {
  operators: z.array(Operator),
  channels: z.array(z.string()),
};
const dimensional = {
  ...filtering,
  describes: Describes,
};

const named = {
  name: z.string(),
  title: z.string(),
  shortTitle: z.string(),
  description: z.string().optional(),
  isVisible: z.boolean().optional(),
  public: z.boolean().optional(),
  aliasMember: z.string().optional(),
};

// Cube's CubeToMetaTransformer sets aggType to `def.aggType || def.type`, wider than the client's TCubeMeasure.
export const AggType = z.enum([
  "count",
  "number",
  "string",
  "boolean",
  "time",
  "sum",
  "avg",
  "min",
  "max",
  "countDistinct",
  "countDistinctApprox",
  "numberAgg",
  "rank",
]);
export type AggType = z.infer<typeof AggType>;

type RuntimeMetaResponse = Omit<MetaResponse, "cubes"> & {
  cubes: (Omit<Cube, "measures"> & {
    measures: (Omit<TCubeMeasure, "aggType"> & { aggType: AggType })[];
  })[];
};

const measureFields = {
  aliasMember: z.string(),
  aggType: AggType,
  cumulative: z.literal(false),
  cumulativeTotal: z.literal(false),
  drillMembers: unsupportedWhenFilled("drill-members-unsupported"),
  drillMembersGrouped: z.object({
    measures: unsupportedWhenFilled("drill-members-unsupported"),
    dimensions: unsupportedWhenFilled("drill-members-unsupported"),
  }),
  format: z.enum(["currency", "percent"]).optional(),
  currency: z.string().optional(),
};

const dimensionFields = {
  currency: z.string().optional(),
  key: z.string().optional(),
  primaryKey: z.boolean().optional(),
};

const MeasureSchema = z.union([
  z.object({
    ...named,
    ...measureFields,
    type: z.literal("number"),
    meta: z.object({
      kind: z.literal("measure"),
      ...filtering,
      polarity: MetricPolarity,
      format: MetricFormat,
      place: z
        .array(MeasurePlace)
        .refine((place) => place.length === 0 || place.includes("metric"), {
          message: "A measure placed anywhere is also placed as a metric",
        }),
      whenEmpty: WhenEmpty,
    }),
  }),
  z.object({
    ...named,
    ...measureFields,
    type: z.literal("string"),
    meta: z.object({ kind: z.literal("value-list"), ...filtering }),
  }),
  z.object({
    ...named,
    ...measureFields,
    type: z.literal("string"),
    meta: z.object({ kind: z.literal("creative-media"), ...filtering }),
  }),
  z.object({
    ...named,
    ...measureFields,
    type: z.literal("string"),
    meta: z.object({
      kind: z.literal("nested"),
      tieBreakers: z.array(z.string()),
      ...filtering,
    }),
  }),
]);

const unescapeListItem = (item: string) =>
  item.replace(/%2C|%25/g, (escaped) => (escaped === "%2C" ? "," : "%"));

export type VideoSource =
  | { kind: "file"; url: string }
  | { kind: "youtube"; videoId: string; thumbnailUrl: string };

export type MediaItem =
  | { kind: "image"; url: string }
  | { kind: "video"; video: VideoSource };

export type CreativeMedia = { creativeId: string; item: MediaItem };

const creativeMediaItem = z.union([
  z.object({
    creative_id: z.string(),
    type: z.literal("image"),
    url: z.string(),
  }),
  z.object({
    creative_id: z.string(),
    type: z.literal("video"),
    source: z.literal("file"),
    url: z.string(),
  }),
  z.object({
    creative_id: z.string(),
    type: z.literal("video"),
    source: z.literal("youtube"),
    video_id: z.string(),
  }),
]);

type CreativeMediaItem = z.output<typeof creativeMediaItem>;

export const creativeMediaJsonSchema = z
  .string()
  .transform((raw, ctx): unknown => {
    try {
      return JSON.parse(raw);
    } catch {
      ctx.addIssue({ code: "custom", message: "Creative media is not JSON" });
      return z.NEVER;
    }
  })
  .pipe(z.array(creativeMediaItem));

const toVideoSource = (
  item: Extract<CreativeMediaItem, { type: "video" }>,
): VideoSource => {
  switch (item.source) {
    case "file": {
      return { kind: "file", url: item.url };
    }
    case "youtube": {
      return {
        kind: "youtube",
        videoId: item.video_id,
        thumbnailUrl: youtubeThumbnailUrl(item.video_id),
      };
    }
    default: {
      return item satisfies never;
    }
  }
};

const toMediaItem = (item: CreativeMediaItem): MediaItem => {
  switch (item.type) {
    case "image": {
      return { kind: "image", url: item.url };
    }
    case "video": {
      return { kind: "video", video: toVideoSource(item) };
    }
    default: {
      return item satisfies never;
    }
  }
};

export const creativeMediaSchema = creativeMediaJsonSchema.transform(
  (items): readonly CreativeMedia[] =>
    items.map((item) => ({
      creativeId: item.creative_id,
      item: toMediaItem(item),
    })),
);

// Snowflake's LISTAGG returns '' for a group with no values, where Postgres returns NULL.
export const valueListSchema = z
  .string()
  .nullable()
  .transform((joined): readonly string[] =>
    joined === null || joined === ""
      ? []
      : joined.split(",").map(unescapeListItem).toSorted(),
  );

const DimensionSchema = z.union([
  z.object({
    ...named,
    ...dimensionFields,
    type: z.literal("string"),
    suggestFilterValues: z.literal(false),
    format: z.literal("imageUrl").optional(),
    meta: z.discriminatedUnion("kind", [
      z.object({
        kind: z.literal("listed"),
        ...dimensional,
        values: ListedValues,
      }),
      z.object({ kind: z.literal("free-text"), ...dimensional }),
    ]),
  }),
  z.object({
    ...named,
    ...dimensionFields,
    type: z.literal("string"),
    suggestFilterValues: z.literal(true),
    format: dimensionFormat.optional(),
    meta: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("queried"), ...dimensional }),
      z.object({
        kind: z.literal("tag"),
        ...dimensional,
        tagColumn: z.string(),
      }),
    ]),
  }),
  z.object({
    ...named,
    ...dimensionFields,
    type: z.literal("number"),
    suggestFilterValues: z.literal(false),
    format: z.enum(["percent", "abbr"]),
    meta: z.object({ kind: z.literal("number"), ...dimensional }),
  }),
  z.object({
    ...named,
    ...dimensionFields,
    type: z.literal("number"),
    suggestFilterValues: z.literal(false),
    meta: z.object({ kind: z.literal("video-length"), ...dimensional }),
  }),
  z.object({
    ...named,
    ...dimensionFields,
    type: z.literal("time"),
    suggestFilterValues: z.literal(false),
    format: dimensionFormat.optional(),
    granularities: z
      .array(z.object({ name: z.string(), title: z.string() }))
      .optional(),
    meta: z.object({ kind: z.literal("time"), ...dimensional }),
  }),
]);

const SegmentSchema = z.object({
  ...named,
  meta: z.object({ initially: SegmentInitially }),
});

type Offer = "offered" | "unusable" | "text-without-contains" | "missing";

const FOLDER_PROBLEMS: Record<Exclude<Offer, "offered">, string> = {
  unusable: "lists a member no filter or column can use",
  "text-without-contains":
    "lists a text dimension that does not offer contains",
  missing: "names no member of its view",
};

const dimensionOffer = (meta: CubeDimension["meta"]): Offer => {
  switch (meta.kind) {
    case "listed":
    case "queried":
    case "time":
    case "tag":
    case "video-length": {
      return "offered";
    }
    case "number": {
      return "unusable";
    }
    case "free-text": {
      return meta.operators.includes("contains")
        ? "offered"
        : "text-without-contains";
    }
    default: {
      return meta satisfies never;
    }
  }
};

const refKindsOf = (member: CubeMember): readonly RefKind[] => {
  switch (member.meta.kind) {
    case "measure": {
      return member.meta.place.includes("metric")
        ? ["measure", "metric"]
        : ["measure"];
    }
    case "value-list": {
      return ["value-list-measure"];
    }
    case "creative-media": {
      return ["creative-media-measure"];
    }
    case "nested": {
      return ["nested-measure"];
    }
    case "number": {
      return ["dimension", "number-dimension"];
    }
    case "video-length": {
      return [];
    }
    case "time": {
      return ["dimension", "group-dimension", "time"];
    }
    case "listed":
    case "free-text":
    case "queried":
    case "tag": {
      return ["dimension", "group-dimension"];
    }
    default: {
      return member.meta satisfies never;
    }
  }
};

const memberRef =
  (view: {
    name: string;
    members: readonly CubeMember[];
    segments: readonly CubeSegment[];
  }): Ref =>
  (kind) => {
    const kinds = new Map<string, readonly RefKind[]>([
      ...view.members.map((member): [string, readonly RefKind[]] => [
        member.name,
        refKindsOf(member),
      ]),
      ...view.segments.map((segment): [string, readonly RefKind[]] => [
        segment.name,
        ["segment"],
      ]),
    ]);
    return z.string().refine((name) => kinds.get(name)?.includes(kind), {
      error: (issue) => `${view.name} has no ${kind} ${String(issue.input)}`,
    });
  };

const folderIssues = (
  view: Pick<CubeView, "measures" | "dimensions" | "folders">,
): string[] => {
  const offers = new Map<string, Offer>([
    ...view.measures.map((measure): [string, Offer] => [
      measure.name,
      measure.meta.kind === "measure" ? "offered" : "unusable",
    ]),
    ...view.dimensions.map((dimension): [string, Offer] => [
      dimension.name,
      dimensionOffer(dimension.meta),
    ]),
  ]);
  return view.folders.flatMap((folder) =>
    folder.members.flatMap((name) => {
      const offer = offers.get(name) ?? "missing";
      return offer === "offered"
        ? []
        : [`Folder ${folder.name} ${FOLDER_PROBLEMS[offer]}: ${name}`];
    }),
  );
};

type LooseView = Pick<
  z.output<typeof baseViewSchema>,
  "measures" | "dimensions" | "segments" | "folders"
> & {
  meta: { controls: unknown; layouts: unknown; entities: unknown };
  hierarchies: { levels: unknown }[];
};

const fatiguingStatusesSchema = z.object({
  fatigue: z.object({ status: z.string(), fatiguing: z.array(z.string()) }),
});

const unlistedFatigueIssues = ({
  layouts,
  members,
}: {
  layouts: unknown;
  members: readonly CubeMember[];
}): readonly string[] => {
  const parsed = fatiguingStatusesSchema.safeParse(layouts);
  if (!parsed.success) {
    return [];
  }
  const { status, fatiguing } = parsed.data.fatigue;
  const listed = members.flatMap((member) =>
    member.name === status && member.meta.kind === "listed"
      ? member.meta.values.map(({ value }) => value)
      : [],
  );
  return fatiguing.flatMap((value) =>
    listed.includes(value)
      ? []
      : [`Fatiguing status ${value} is not a listed value of ${status}`],
  );
};

const checkView =
  <Layouts extends z.ZodType, Entities extends EntitiesSchema>({
    name,
    records,
  }: {
    name: string;
    records: Records<Layouts, Entities>;
  }) =>
  (
    view: LooseView,
    ctx: { addIssue: (issue: { code: "custom"; message: string }) => void },
  ) => {
    const members: CubeMember[] = [...view.measures, ...view.dimensions];
    const outside = [...members, ...view.segments].flatMap((member) =>
      member.name.startsWith(`${name}.`)
        ? []
        : [`Member ${member.name} is not named in ${name}`],
    );
    const ref = memberRef({ name, members, segments: view.segments });
    const controls = controlsSchema(ref).safeParse(view.meta.controls);
    const layouts = records.layouts(ref).safeParse(view.meta.layouts);
    const entities = records.entities(ref).safeParse(view.meta.entities);
    const recordIssues = [controls, layouts, entities].flatMap((parsed) =>
      parsed.success ? [] : parsed.error.issues.map((issue) => issue.message),
    );
    const fatigueIssues = layouts.success
      ? unlistedFatigueIssues({ layouts: layouts.data, members })
      : [];
    const drill = entities.success ? drillLevelsOf(entities.data) : [];
    const drillIssues =
      entities.success &&
      JSON.stringify(view.hierarchies[0]?.levels) !== JSON.stringify(drill)
        ? [`The drill hierarchy of ${name} is not ${drill.join(", ")}`]
        : [];
    for (const message of [
      ...outside,
      ...folderIssues(view),
      ...recordIssues,
      ...fatigueIssues,
      ...drillIssues,
    ]) {
      ctx.addIssue({ code: "custom", message });
    }
  };

const nestedFolder: z.ZodType<TCubeNestedFolder> = z.object({
  name: z.string(),
  get members() {
    return z.array(z.union([z.string(), nestedFolder]));
  },
});

const viewGroup: z.ZodType<ViewGroup> = z.object({
  name: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  views: z.array(z.string()),
  get includes() {
    return z.array(z.union([z.string(), viewGroup])).optional();
  },
});

const baseViewSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  connectedComponent: z.number().optional(),
  isVisible: z.boolean().optional(),
  public: z.boolean().optional(),
  viewGroups: z.array(z.string()).optional(),
  type: z.literal("view"),
  measures: z.array(MeasureSchema),
  dimensions: z.array(DimensionSchema),
  segments: z.array(SegmentSchema),
  folders: z.array(
    z.object({ name: z.string(), members: z.array(z.string()) }),
  ),
  nestedFolders: z
    .array(nestedFolder)
    .refine((folders) => folders.length === 0, {
      message: "nested-folders-unsupported",
    }),
});

const hierarchiesSchema = z
  .array(
    z.object({
      name: z.string(),
      title: z.string().optional(),
      levels: z.array(z.string()),
      public: z.boolean().optional(),
    }),
  )
  .length(1, { message: "one-hierarchy" });

const adViewSchema = baseViewSchema
  .extend({
    name: z.literal("ad_performance"),
    meta: ViewMetaSchema.extend({
      layouts: adLayoutsSchema(anyName),
      entities: adEntitiesSchema(anyName),
    }),
    hierarchies: hierarchiesSchema,
  })
  .superRefine(
    checkView({ name: "ad_performance", records: VIEW_RECORDS.ad_performance }),
  );
const adSetViewSchema = baseViewSchema
  .extend({
    name: z.literal("ad_set_performance"),
    meta: ViewMetaSchema.extend({
      layouts: adSetLayoutsSchema(anyName),
      entities: adSetEntitiesSchema(anyName),
    }),
    hierarchies: hierarchiesSchema,
  })
  .superRefine(
    checkView({
      name: "ad_set_performance",
      records: VIEW_RECORDS.ad_set_performance,
    }),
  );
const campaignViewSchema = baseViewSchema
  .extend({
    name: z.literal("campaign_performance"),
    meta: ViewMetaSchema.extend({
      layouts: campaignLayoutsSchema(anyName),
      entities: campaignEntitiesSchema(anyName),
    }),
    hierarchies: hierarchiesSchema,
  })
  .superRefine(
    checkView({
      name: "campaign_performance",
      records: VIEW_RECORDS.campaign_performance,
    }),
  );

export const cubeMetaSchema = z.object({
  viewGroups: z.array(viewGroup).optional(),
  cubes: z
    .union([
      z.tuple([]).transform((): [] => []),
      z
        .tuple([adViewSchema, adSetViewSchema, campaignViewSchema])
        .transform(
          ([ad, adSet, campaign]): [
            z.output<typeof adViewSchema>,
            z.output<typeof adSetViewSchema>,
            z.output<typeof campaignViewSchema>,
          ] => [ad, adSet, campaign],
        ),
    ])
    .nonoptional(),
}) satisfies z.ZodType<RuntimeMetaResponse>;

export type CubeMeasure = z.output<typeof MeasureSchema>;
export type CubeDimension = z.output<typeof DimensionSchema>;
export type CubeMember = CubeMeasure | CubeDimension;
export type CubeSegment = z.output<typeof SegmentSchema>;
export type MeasureMeta = Extract<CubeMeasure["meta"], { kind: "measure" }>;
export type AdView = z.output<typeof adViewSchema>;
export type AdSetView = z.output<typeof adSetViewSchema>;
export type CampaignView = z.output<typeof campaignViewSchema>;
export type CubeView = AdView | AdSetView | CampaignView;
export type CubeMeta = z.output<typeof cubeMetaSchema>;
